<template>
  <div id="videoDiv" ref="videoDiv" v-if="this.playoutContent">
    <video
      class="video-js"
      :id="playerId"
      :ref="playerId"
      preload="metadata"
      playsinline
      controls
      :poster="this.playoutContent.poster"
      :data-setup="this.playoutContent.dataSetup"
    >
      <source :src="this.playoutContent.manifestUrl" :type="this.playoutContent.type">
    </video>
  </div>
</template>

<script>
import 'videojs-ima'
import { mapState } from 'vuex'
import { spinnakerApi } from '../../js/SpinnakerApi.js'
import { VideoEvents } from '../../js/VideoEvents.js'
const videoEvents = new VideoEvents()

export default {
  data: function () {
    return {
      salt: Math.floor((Math.random() * 10000)),
      player: undefined,
      debug: false, // !spinnaker.isProd()
      isActive: true,
      clientStreamId: null,
      isFirstTimePlay: true,
      adsPlaying: false,
      isPlaying: false,
      needsReset: false,
      waitingForPostRollAd: false,
      postRollAdTimer: null
    }
  },
  props: ['progId', 'playout', 'playoutContent', 'active'],
  watch: {
    active: function (newValue, oldValue) {
      if (newValue === true) {
        this.handleActivate()
      } else if (newValue === false) {
        this.handleDeactivate()
      }
    }
  },
  computed: {
    ...mapState({
      share: state => state.share
    }),
    playerId () {
      return `videoPlayer-${this.progId}-${this.salt}`
    }
  },
  mounted () {
    console.info(`player.mounted: [${this.progId}] ${this.playoutContent.title}`)
    this.playerInit()
  },
  beforeDestroy () {
    console.info(`player.beforeDestroy: [${this.progId}] ${this.playoutContent.title}`)
    this.playerTeardown()
  },
  methods: {
    //
    // Event handling
    //

    handleActivate () {
      this.player.ready(async () => {
        console.info(`player.handleActivate: [${this.progId}] ${this.playoutContent.title}`)
        const videoPlayerEl = this.getPlayerElement()
        videoPlayerEl.classList.add('vjs-has-started')
        videoPlayerEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
        this.isActive = true
        this.isPlaying = false
        this.isFirstTimePlay = true
        this.adsPlaying = false
        this.clientStreamId = false
        this.waitingForPostRollAd = false

        // don't wait for completion of this async function (in order to start faster)
        this.performSpinnakerStartInteractions()

        if (this.needsReset) {
          console.info(`player.handleActivate: carrying out reset for ${JSON.stringify(this.playoutContent)}`)

          this.player.reset()
          this.player.src({
            type: this.playoutContent.type,
            src: this.playoutContent.manifestUrl,
            withCredentials: false
          })

          this.needsReset = false
        }

        if (this.playoutContent.ads) {
          this.getPlayerElement().setAttribute('autoplay', '')
          this.player.ima.initializeAdDisplayContainer()
        }

        try {
          this.player.currentTime(0)
          await this.player.play()
          console.info(`player: handleActivate [${this.progId}] ${this.playoutContent.title}: player started successfully`)
        } catch (err) {
          console.info(`player: handleActivate error [${this.progId}] ${this.playoutContent.title}: ${err}`)
        }
      }, true)
    },

    handleDeactivate () {
      this.player.ready(() => {
        console.info(`player.handleDeactivate: [${this.progId}] ${this.playoutContent.title} (started playing yet? ${!this.isFirstTimePlay}; isActive? ${this.isActive})`)

        if (!this.isActive) {
          console.info(`player.handleDeactivate: already inactive - nothing to do`)
          return
        }

        if (this.adsPlaying) {
          this.player.ima.pauseAd()
          this.needsReset = true
        }

        // only send stop events if something has actually started playing
        if (!this.isFirstTimePlay) {
          videoEvents.playStopped(
            this.playoutContent,
            this.clientStreamId
          )

          spinnakerApi.stopPlayout(
            this.playoutContent,
            this.player.currentTime()
          )
        }

        this.player.pause()
        if (this.player.isFullscreen()) {
          this.player.exitFullscreen()
          this.player.exitFullWindow()
        }
        this.clearPostRollAdTimer()
        this.isActive = false
      }, true)
    },

    async playerEventListener (event) {
      const adsMode = typeof this.player.ads !== 'undefined' &&
        typeof this.player.ads.isInAdMode !== 'undefined' &&
        this.player.ads.isInAdMode()

      if (!this.isFirstTimePlay && this.isPlaying) {
        videoEvents.postEvent(
          { type: event, position: this.player.currentTime() },
          this.clientStreamId
        )
      }

      switch (event) {
        case 'ended':
          if (adsMode) {
            // Mobile Safari generates an ended event at end of ad: ignore
          } else if (this.playoutContent.ads && !this.waitingForPostRollAd) {
            // give postroll a chance to start before quitting
            this.waitingForPostRollAd = true
            this.postRollAdTimer = setTimeout(this.handlePostRollAdTimer, 3000)
          } else {
            this.getPlayerElement().classList.remove('vjs-has-started')
            this.$store.commit('unmuteNotifications')
            this.$emit('ended')
          }
          break
        case 'playing':
          this.isPlaying = true
          this.$store.commit('muteNotifications')
          break
        case 'play':
          this.isPlaying = true
          this.isFirstTimePlay = false
          this.$store.commit('muteNotifications')
          break
        case 'pause':
          this.isPlaying = false
          this.$store.commit('unmuteNotifications')
          break
        case 'waiting':
          this.isPlaying = false
          break
        case 'canplay':
          break
        case 'error':
          console.info(`player error: ${JSON.stringify(this.player.error(), null, 4)}`)
          this.needsReset = true
          this.$store.commit('unmuteNotifications')
          this.$emit('error')
          break
        case 'fullscreenchange':
          this.$emit('fullscreenchanged', this.player.isFullscreen())
          break
        default:
          break
      }
    },

    //
    // Player lifecycle
    //

    playerInit () {
      this.player = window.videojs(this.$refs[this.playerId], {
        errorDisplay: false,
        // the below one is to make sure the video size is responsive
        fluid: true
      })
      this.player.landscapeFullscreen({
        fullscreen: {
          enterOnRotate: true,
          alwaysInLandscapeMode: true,
          iOS: true
        }
      })
      this.playerListenEvents(true)

      if (this.playoutContent.ads && this.playoutContent.ads.length > 0) {
        // Use hardcoded url until ready for adsense
        const randomAdsIndex = Math.floor(Math.random() * 1000) % this.playoutContent.ads.length
        const adsUrl = this.playoutContent.ads[randomAdsIndex].url
        const imaOptions = {
          adTagUrl: adsUrl,
          debug: !spinnakerApi.isProd(),
          timeout: 10000,
          prerollTimeout: 10000,
          contribAdsSettings: {
            prerollTimeout: 10000
          },
          adsManagerLoadedCallback: this.adsManagerLoadedCallback.bind(this)
        }
        console.info(`Using advertisement URL: ${adsUrl}`)
        this.player.ima(imaOptions)
      }
    },

    playerTeardown () {
      console.info(`player.playerTeardown: [${this.progId}] ${this.playoutContent.title}`)
      this.handleDeactivate()
      this.playoutContent.dataSetup = null
      if (this.player) {
        this.player.pause()
        this.playerListenEvents(false)
        this.player.dispose()
        this.player = null
      }
    },

    playerListenEvents (register) {
      let events = ['loadedmetadata']
      if (!window.VIDEO_EVENTS_OFF) {
        events = events.concat([
          'waiting',
          'seeked',
          'ended',
          'play',
          'pause',
          'fullscreenchange'
        ])
      }
      const debugEvents = [
        'loadstart',
        'progress',
        'suspend',
        'abort',
        'error',
        'emptied',
        'stalled',
        'loadedmetadata',
        'loadeddata',
        'canplay',
        'canplaythrough',
        'playing',
        'waiting',
        'seeking',
        'seeked',
        'ended',
        'durationchange',
        'timeupdate',
        'play',
        'pause',
        'ratechange',
        'resize',
        'volumechange'
      ]
      const fn = register ? 'on' : 'off'
      const target = this.debug ? debugEvents : events
      target.forEach(
        function (event, index) {
          this.player[fn](event, this.playerEventListener.bind(this, event))
        }.bind(this)
      )
    },

    //
    // Ad handling
    //

    adsManagerLoadedCallback () {
      const events = [
        window.google.ima.AdEvent.Type.COMPLETE,
        window.google.ima.AdEvent.Type.LOADED
      ]
      events.forEach(event => {
        this.player.ima.addEventListener(event, this.onAdEvent.bind(this))
      })

      this.player.on('adslog', this.onAdLog.bind(this))
    },

    onAdEvent (event) {
      console.info(`player.onAdEvent: [${this.progId}] ${this.playoutContent.title} - ${event.type}`)
      switch (event.type) {
        case window.google.ima.AdEvent.Type.LOADED:
          this.adsPlaying = true

          if (this.waitingForPostRollAd) {
            console.info(`player.onAdEvent: starting a post-roll (cancelling timer)`)
            this.clearPostRollAdTimer()
          }
          break
        case window.google.ima.AdEvent.Type.COMPLETE:
          this.adsPlaying = false
          break
      }
    },

    onAdLog (data) {
      console.info(`player.onAdLog: [${this.progId}] ${this.playoutContent.title} - ${data.data.AdError}`)
      this.needsReset = true
      this.$store.commit('unmuteNotifications')
      this.$emit('error')
    },

    handlePostRollAdTimer () {
      console.info(`player.handlePostRollAdTimer: timer expired without a post-roll: ending playback`)
      this.getPlayerElement().classList.remove('vjs-has-started')
      this.$store.commit('unmuteNotifications')
      this.$emit('ended')
    },

    clearPostRollAdTimer () {
      if (this.postRollAdTimer) {
        clearTimeout(this.postRollAdTimer)
        this.postRollAdTimer = null
      }
    },

    //
    // Misc
    //

    getPlayerElement () {
      return document.querySelector(`#${this.playerId}`)
    },

    async performSpinnakerStartInteractions () {
      this.clientStreamId = videoEvents.playInit(
        this.playoutContent,
        this.player
      )

      const streamStartDetails = await spinnakerApi.startStream(
        this.playoutContent,
        this.share.shareToken
      )

      this.playoutContent.streamId = streamStartDetails.streamId

      // TODO: defer this until playing event?
      videoEvents.playStarted(
        this.playoutContent,
        this.playoutContent.streamId,
        this.player,
        this.clientStreamId
      )
    }
  }
}
</script>
